// Lib
import { merge, isEmpty, uniq, isEqual } from 'lodash/fp';

// Utils
import {
    getNotificationActorIdsAsArray,
    getNotificationBoardTitle,
    getNotificationElementId,
    getNotificationId,
    getNotificationTimestamp,
    getNotificationType,
    getNotificationUserId,
    getNotificationViewedTimestamp,
    isBoardViewNotification,
} from '../../../../../../common/notifications/notificationModelUtils';
import getNotificationGroup, { getNotificationParentGroup } from './getNotificationGroup';
import { length } from '../../../../../../common/utils/immutableHelper';

// Selectors
import { getElements } from '../../../../../element/selectors/elementsSelector';
import { createDeepSelector } from '../../../../../utils/milanoteReselect/milanoteReselect';
import cleansedNotificationsSelector from '../../../../../notifications/cleansedNotificationsSelector';
import { orderNotificationsByDescendingTimestamp } from '../../../../../notifications/notificationsSelector';
import { getCurrentUserId } from '../../../../../user/currentUserSelector';
import { getGroupId, getGroupNotifications } from './notificationGroupUtils';

// Constants
import { NOTIFICATION_TYPES } from '../../../../../../common/notifications/notificationConstants';

const isBoardModificationNotification = (notification) => {
    const type = getNotificationType(notification);

    switch (type) {
        case NOTIFICATION_TYPES.ASSIGNMENT:
        case NOTIFICATION_TYPES.MENTION:
        case NOTIFICATION_TYPES.COMMENT:
        case NOTIFICATION_TYPES.BOARD_UPDATE:
        case NOTIFICATION_TYPES.BOARD_TASKS_UPDATE:
        case NOTIFICATION_TYPES.BOARD_TASKS_REMINDER:
            return true;
        default:
            return false;
    }
};

const areGroupsEqual = (groupA, groupB) => {
    if (!groupA || !groupB) return false;
    if (groupA.groupType !== groupB.groupType) return false;
    if (groupA.id && groupB.id && groupA.id === groupB.id) return true;
    return groupA.title === groupB.title;
};

const groupNotifications = ({ elements, notifications, currentUserId }) => {
    let currentGroup = {};
    let currentGroupProperties = {};
    let immediateGroup = null;
    let parentGroup = null;
    let depth = 0;
    let index = -1;

    return notifications.sort(orderNotificationsByDescendingTimestamp).reduce((acc, notification) => {
        index++;

        const notificationImmediateGroup = getNotificationGroup({ elements, notification, userId: currentUserId });
        const notificationParentGroup = getNotificationParentGroup({
            elements,
            notification,
            userId: currentUserId,
        });

        let groupsMatch = false;
        let matchingGroup = null;

        if (currentGroup) {
            // Check if the current group matches the previous group
            if (areGroupsEqual(currentGroup, notificationImmediateGroup)) {
                // No changes to the groups and the groups match
                groupsMatch = true;
                matchingGroup = notificationImmediateGroup;
            } else if (notificationParentGroup && areGroupsEqual(currentGroup, notificationParentGroup)) {
                // The parent group of the next element is the same as the current group
                // So we're now grouping with a depth of 1 and the current group has to be the group we use
                groupsMatch = true;
                depth = 1;
                matchingGroup = notificationParentGroup;
            } else if (depth === 0 && parentGroup && areGroupsEqual(parentGroup, notificationImmediateGroup)) {
                groupsMatch = true;
                currentGroup = parentGroup;
                parentGroup = null;
                depth = 1;
                matchingGroup = notificationImmediateGroup;
            } else if (depth === 0 && parentGroup && areGroupsEqual(parentGroup, notificationParentGroup)) {
                groupsMatch = true;
                currentGroup = parentGroup;
                parentGroup = null;
                depth = 1;
                matchingGroup = notificationParentGroup;
            }
        }

        // Merge their data if groups match
        if (groupsMatch) {
            merge(currentGroup, matchingGroup);
        } else {
            // Otherwise save the current group if there is one and then start the next group
            if (currentGroup) {
                // If changing group we now know all we need to know about the previous group in order to merge it
                const groupDetails = {
                    ...currentGroup,
                    ...currentGroupProperties,
                };

                acc.push(groupDetails);
            }

            // Initialise new group data
            depth = 0;
            immediateGroup = notificationImmediateGroup;
            parentGroup = notificationParentGroup;
            // By default use the current immediate group.
            // Only use the parent group if it helps grouping more notifications
            currentGroup = immediateGroup;

            // The timestamp is the newest notification's timestamp
            currentGroupProperties = {
                timestamp: getNotificationTimestamp(notification),
            };
        }

        currentGroupProperties.unseen =
            currentGroupProperties.unseen ||
            (!getNotificationViewedTimestamp(notification) && !isBoardViewNotification(notification));
        currentGroupProperties.notifications = currentGroupProperties.notifications || [];
        currentGroupProperties.notifications.push(notification);

        // If we've reached the last group then we should also add that group
        if (index === length(notifications) - 1) {
            const groupDetails = {
                ...currentGroup,
                ...currentGroupProperties,
            };

            acc.push(groupDetails);
        }

        return acc;
    }, []);
};

const buildGroupedNotification = ({ notificationIds, notifications }) =>
    notifications.reduce(
        (acc, notification) => {
            if (!notificationIds.includes(getNotificationId(notification))) return acc;

            const notificationId = getNotificationId(notification);

            acc._id = acc._id ? `${acc._id}_${notificationId}` : notificationId;

            acc.actorIds = acc.actorIds || getNotificationActorIdsAsArray(notification);
            acc.userId = acc.userId || getNotificationUserId(notification);

            const timestamp = getNotificationTimestamp(notification);
            acc.timestamp = !acc.timestamp || timestamp > acc.timestamp ? timestamp : acc.timestamp;

            acc.originalNotifications.push(notification);

            acc.viewedElements.push({
                elementId: getNotificationElementId(notification),
                boardTitle: getNotificationBoardTitle(notification),
            });

            return acc;
        },
        {
            _id: null,
            actorIds: null,
            userId: null,
            type: NOTIFICATION_TYPES.CLIENT_ONLY_GROUPED_BOARD_VIEW,
            originalNotifications: [],
            viewedElements: [],
            timestamp: null,
        },
    );

const getActorIdsGroupKey = (notification) => {
    const actorIds = getNotificationActorIdsAsArray(notification);
    return actorIds.sort().join('_');
};

const orderByGroupThenTimestamp = (groupId) => (notificationA, notificationB) => {
    const notificationAElementId = getNotificationElementId(notificationA);
    const notificationBElementId = getNotificationElementId(notificationB);

    const notificationAIsGroup = notificationAElementId === groupId;
    const notificationBIsGroup = notificationBElementId === groupId;

    if (notificationAIsGroup && !notificationBIsGroup) return -1;
    if (notificationBIsGroup && !notificationAIsGroup) return 1;

    return orderNotificationsByDescendingTimestamp(notificationA, notificationB);
};

/**
 * For each group will process the notifications list to build
 */
const parseGroupedNotifications = (groups) =>
    groups.map((group) => {
        const notifications = getGroupNotifications(group);
        const groupId = getGroupId(group);

        let viewNotificationIdsToRemove = [];

        const groupedViewsMap = {};
        const actorsByBoardId = {};

        notifications.forEach((notification) => {
            const actorIds = getNotificationActorIdsAsArray(notification);
            const elementId = getNotificationElementId(notification);

            // If the notification is a board modification keep track of the actors who are doing the modifying
            // so that we can hide a view notification with the same actors
            if (isBoardModificationNotification(notification)) {
                actorsByBoardId[elementId] = uniq([...(actorsByBoardId[elementId] || []), ...actorIds]);
            }

            if (!isBoardViewNotification(notification)) return;

            // If the board view notification already has modifications from the same users
            // then ignore it and don't add it to the view groups
            const actorsThatHaveMadeModifications = actorsByBoardId[elementId];

            if (isEqual(actorIds, actorsThatHaveMadeModifications)) {
                viewNotificationIdsToRemove.push(getNotificationId(notification));
                return;
            }

            // We don't group "this board" views into the other board views
            if (elementId === groupId) return;

            // Otherwise add this notification to the view group
            const groupKey = getActorIdsGroupKey(notification);

            groupedViewsMap[groupKey] = groupedViewsMap[groupKey] || [];

            groupedViewsMap[groupKey].push(getNotificationId(notification));
        });

        const groupedViewNotificationsToAdd = [];

        // Loop over groupedViewsMap and if there's any lists with length > 1 then we need to remove notifications and
        // add replacement notifications
        for (const groupedViewsId in groupedViewsMap) {
            if (!groupedViewsMap.hasOwnProperty(groupedViewsId)) continue;

            const groupedViewNotificationIds = groupedViewsMap[groupedViewsId];

            if (groupedViewNotificationIds.length > 1) {
                viewNotificationIdsToRemove = viewNotificationIdsToRemove.concat(groupedViewNotificationIds);

                const groupedNotification = buildGroupedNotification({
                    notificationIds: groupedViewNotificationIds,
                    notifications,
                });

                groupedViewNotificationsToAdd.push(groupedNotification);
            }
        }

        let notificationsToDisplay = isEmpty(viewNotificationIdsToRemove)
            ? notifications
            : notifications
                  .filter((notification) => !viewNotificationIdsToRemove.includes(getNotificationId(notification)))
                  .concat(groupedViewNotificationsToAdd);

        notificationsToDisplay = notificationsToDisplay.sort(orderByGroupThenTimestamp(groupId));

        return {
            ...group,
            notificationsToDisplay,
        };
    });

export const notificationGroupSelector = createDeepSelector(
    getElements,
    cleansedNotificationsSelector,
    getCurrentUserId,
    (elements, notifications, currentUserId) => {
        const groupedNotifications = groupNotifications({
            elements,
            notifications,
            currentUserId,
        });

        return parseGroupedNotifications(groupedNotifications);
    },
);
